import React, { useContext, useEffect, useState, useCallback } from 'react';
import SectionTitle from "./SectionTitle";
import MultiLingualContent from "../config/multilingualContent";
import { LanguageContext } from "../config/multilingualContext";
import { listIndi, readIndicator, getCountries } from "../functions";
import { SelectPicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import FormattedNumber from './FormattedNumber';
import '../assets/css/dashcontent.css';
import { show as showCountriesOverride } from '../constants/countries.override';
import Loading from './Loading';

const IndicatorContent = () => {
  const { language } = useContext(LanguageContext);

  const [loading, setLoading]                     = useState(false);
  const [isLoading, setIsLoading]                 = useState(false);
  const [indicatorList, setIndicatorList]         = useState([]);
  const [indicatorItems, setIndicatorItems]       = useState([]);
  const [indicatorVal, setIndicatorVal]           = useState("");
  const [country, setCountry]                     = useState("");
  const [countriesForPicker, setCountriesForPicker] = useState([]);
  const [indDetails, setIndDetails]               = useState([]);
  const [indicatorItemLabel, setIndicatorItemLabel] = useState([]);
  const [allData, setAllData]                     = useState([]);
  const [years, setYears]                         = useState([]);

  // -------- MULTILINGUE HELPERS ----------
  const getLabel = (item) => {
    return {
      french:     item.label_fr ?? '',
      english:    item.label_en ?? '',
      portuguais: item.label_pr ?? '',
      swahili:    item.label_sw ?? ''
    }[language] || '';
  };

  const getLabelWithUnit = (item) => {
    return {
      french:     `${item.label_fr} (${item.unit_label_fr})`,
      english:    `${item.label_en} (${item.unit_label_en})`,
      portuguais: `${item.label_pr} (${item.unit_label_pr})`,
      swahili:    `${item.label_sw} (${item.unit_label_sw})`
    }[language] || '';
  };

  // -------- FETCH INDICATOR DATA ----------
  const fetchIndicatorDetails = useCallback(async (indicatorId, countryId) => {
    if (!indicatorId || !countryId) return;

    setLoading(true);
    try {
      const response = await readIndicator(indicatorId, countryId);
      const { data } = response;
      const tableData = data.tableStatDataAPI;

      setIndDetails(tableData.indDetails);
      setIndicatorItemLabel(tableData.indicator_item);
      setAllData(tableData.allData);
      setYears(tableData.years);
    } catch (error) {
      console.error(error);
      setIndDetails([]);
      setIndicatorItemLabel([]);
      setAllData([]);
      setYears([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // -------- LOAD INDICATORS + COUNTRIES ----------
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await listIndi();
        const { data } = response;
        const { indicator, indicatorItem } = data;
        const countries = await getCountries();

        setIndicatorList(indicator);
        setIndicatorItems(indicatorItem);

        const countryOptions = countries.map(item => ({
          value: item.id,
          label: getLabel(item)
        })).sort((a, b) => (a.label || '').localeCompare(
          b.label || '',
          language === 'french' ? 'fr' : language === 'english' ? 'en' : language === 'portuguais' ? 'pt' : 'sw',
          { sensitivity: 'base' }
        ));

        setCountriesForPicker(countryOptions);

        if (indicator?.length && countryOptions?.length) {
          const firstIndicator = indicator[0].id;
          const firstCountry   = countryOptions[0].value;

          setIndicatorVal(firstIndicator);
          setCountry(firstCountry);

          await fetchIndicatorDetails(firstIndicator, firstCountry);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [language]);

  // -------- HANDLERS — requête lancée à chaque changement ----------
  const handleIndicatorChange = (value) => {
    setIndicatorVal(value);
    fetchIndicatorDetails(value, country);
  };

  const handleCountryChange = (value) => {
    setCountry(value);
    fetchIndicatorDetails(indicatorVal, value);
  };

  // -------- RENDER TABLE ----------
  const renderTable = () => {
    if (!allData?.length) return <p><MultiLingualContent contentID="nodata" /></p>;

    return (
      <div style={{ overflowX: 'auto' }}>
        <table className='table table-striped table-bordered'>
          <thead>
            <tr>
              <th className='fixed-td'>{indDetails && getLabel(indDetails)}</th>
              {years?.map((y, idx) => <th className="scrollable-td fixed-hd" key={idx}>{y}</th>)}
            </tr>
          </thead>
          <tbody>
            {indicatorItemLabel?.map((item, idx) => (
              <tr key={idx}>
                <td className='fixed-td text-white'>{getLabelWithUnit(item)}</td>
                {years?.map((yearItem, yearIdx) => (
                  <td className='scrollable-td' key={yearIdx}>
                    {allData[yearIdx] && allData[yearIdx][item.id] ? (
                      <FormattedNumber value={allData[yearIdx][item.id].value} locale={language} decimals={2} />
                    ) : '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="container about-list-items mb-5">
      <SectionTitle pageTitle="indicator" />

      <div className="row mt-2">
        <div className="col-lg-4 col-md-6 mt-2">
          <SelectPicker
            label={<MultiLingualContent contentID="indicator" />}
            data={indicatorList.map(i => ({ label: getLabel(i), value: i.id }))}
            style={{ width: '100%' }}
            value={indicatorVal}
            onChange={handleIndicatorChange}
            required
          />
        </div>

        {!showCountriesOverride && (
          <input type="hidden" name="country" value={country ?? ''} />
        )}

        {showCountriesOverride && (
          <div className="col-lg-4 col-md-6 mt-2">
            <SelectPicker
              label={<MultiLingualContent contentID="country" />}
              data={countriesForPicker}
              style={{ width: '100%' }}
              value={country}
              onChange={handleCountryChange}
            />
          </div>
        )}
      </div>

      <div className="row mt-3">
        <div className="col-12">
          <div className="card">
            <div className="card-body min-vh-100">
              {loading || isLoading
                ? <Loading />
                : renderTable()
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndicatorContent;